import { CircularProgress } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import useService from "../../../../hooks/useService";
import DrawerFiltersListLocal from "../../../cm/Dashboard/FullPages/Reports/components/ReportsDetail/components/DrawerFiltersListLocal";
import { transformFilters } from "../../../cm/Dashboard/FullPages/Reports/components/ReportsDetail/utils/dto";
import ReportTooltip from "../../../cm/Dashboard/FullPages/Reports/components/ReportsList/components/ReportTooltip";
import HNSColors from "../../../ui library/HNSColors";
import DrawerFilters from "../../drawer-filters";
import { useDrawerFiltersState } from "../../drawer-filters/hooks/useDrawerFiltersState";
import { getMetaAndData } from "../helpers/functions";
import useAnalyticsChartData from "../hooks/useAnalyticsChartData";

import GeneriChartCard from "./GenericChartCard";
import MetricCard from "./MetricCard";

// This is to mapping is to overwright the default ReCharts Rendering
const CHART_COMPONENTS = {
  "metric-card": MetricCard,
};
function MessageBox({ children }) {
  return (
    <div
      className="flex min-h-64 w-full items-center justify-center py-6 text-sm"
      style={{ color: HNSColors.neutrals[400] }}
    >
      {children}
    </div>
  );
}

const isEmptyFilterValue = (value) =>
  value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0);

function applyFilterValue(params, key, value) {
  const rawKey = String(key);
  const baseKey = rawKey.replace(/(__in)$/, "");
  const targetKey = Array.isArray(value) && value.length > 1 && !rawKey.endsWith("__in")
    ? `${baseKey}__in`
    : rawKey;

  if (isEmptyFilterValue(value)) {
    params.delete(rawKey);
    params.delete(baseKey);
    params.delete(`${baseKey}__in`);
    return;
  }

  if (Array.isArray(value)) {
    params.delete(targetKey.endsWith("__in") ? baseKey : `${baseKey}__in`);
    params.set(targetKey, value.join(","));
    return;
  }

  params.delete(`${baseKey}__in`);
  params.set(targetKey, value);
}

function getRawFilterDefinitions(response) {
  if (Array.isArray(response)) {
    return response.map((record) => record?.raw ?? record);
  }
  return response?.raw ?? response ?? [];
}

function ChartFilters({ appliedFilters, filtersUrl, onApply }) {
  const analyticsChartRepository = useService("repositories/analytics-chart-repository");
  const [filterConfig, setFilterConfig] = useState([]);
  const [draftFilters, setDraftFilters] = useState(() => new URLSearchParams(appliedFilters));
  const [drawerCloseSignal, setDrawerCloseSignal] = useState(0);
  const [loading, setLoading] = useState(false);

  const { filterState } = useDrawerFiltersState({
    filterConfig,
    filters: draftFilters,
  });

  const loadFilters = useCallback(async () => {
    if (!filtersUrl || filterConfig.length > 0 || loading) {
      setDraftFilters(new URLSearchParams(appliedFilters));
      return;
    }

    setLoading(true);
    try {
      const response = await analyticsChartRepository.fetchByUrl(filtersUrl);
      setFilterConfig(transformFilters(getRawFilterDefinitions(response)));
      setDraftFilters(new URLSearchParams(appliedFilters));
    } finally {
      setLoading(false);
    }
  }, [analyticsChartRepository, appliedFilters, filterConfig.length, filtersUrl, loading]);

  const updateDraftFilter = useCallback((key, value, bulkNewFilters = null) => {
    const next = bulkNewFilters || new URLSearchParams(draftFilters);
    applyFilterValue(next, key, value);
    if (!bulkNewFilters) {
      setDraftFilters(new URLSearchParams(next));
    }
  }, [draftFilters]);

  const bulkUpdateDraftFilters = useCallback((filterMap) => {
    const next = new URLSearchParams(draftFilters);
    for (const [filterKey, filterValue] of filterMap) {
      applyFilterValue(next, filterKey, filterValue);
    }
    setDraftFilters(next);
  }, [draftFilters]);

  const applyDraftFilters = () => {
    onApply(new URLSearchParams(draftFilters));
    setDrawerCloseSignal((previous) => previous + 1);
  };

  const clearDraftFilters = () => {
    const emptyFilters = new URLSearchParams();
    setDraftFilters(emptyFilters);
    onApply(emptyFilters);
    setDrawerCloseSignal((previous) => previous + 1);
  };

  if (!filtersUrl) {
    return null;
  }
  const filterId = String(filtersUrl).replace(/[^a-zA-Z0-9_-]/g, "-");

  return (
    <DrawerFilters
      buttonTittle="Filters"
      drawTittle="Chart filters"
      triggerId={`analytics-chart-filters-button-${filterId}`}
      closeButtonId={`analytics-chart-filters-close-button-${filterId}`}
      buttonSx={{ height: "32px" }}
      closeSignal={drawerCloseSignal}
      onOpen={loadFilters}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-hidden">
          {loading ? (
            <MessageBox>
              <CircularProgress color="primary" />
            </MessageBox>
          ) : (
            <DrawerFiltersListLocal
              filterState={filterState}
              updateFilter={updateDraftFilter}
              bulkUpdateFilters={bulkUpdateDraftFilters}
            />
          )}
        </div>
        <div className="border-t border-gray-200 p-3">
          <div className="flex gap-2">
            <button
              id={`analytics-chart-filters-clear-${filterId}`}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              onClick={clearDraftFilters}
            >
              Clear
            </button>
            <button
              id={`analytics-chart-filters-apply-${filterId}`}
              className="w-full rounded bg-sky-600 px-3 py-2 text-sm text-white"
              onClick={applyDraftFilters}
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </DrawerFilters>
  );
}

export default function AnalyticsChart({ chart }) {
  const type = chart?.meta?.chart_type;
  const Component = CHART_COMPONENTS[type] || GeneriChartCard;
  const { title, description, dimensions, measures, dataUrl, filtersUrl } = getMetaAndData(chart);
  const [appliedFilters, setAppliedFilters] = useState(() => new URLSearchParams());
  const chartFilters = useMemo(() => new URLSearchParams(appliedFilters), [appliedFilters]);
  const { isLoading, error, record } = useAnalyticsChartData(dataUrl, chartFilters);
  return (
    <div className="HNSChart h-full w-full">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold">{title}</div>
          {description ? <ReportTooltip description={description} allowHover /> : <div />}
        </div>
        <ChartFilters
          appliedFilters={appliedFilters}
          filtersUrl={filtersUrl}
          onApply={setAppliedFilters}
        />
      </div>

      {isLoading ? (
        <MessageBox>
          <CircularProgress color="primary" />
        </MessageBox>
      ) : error ? (
        <MessageBox>
          <span>Failed to load chart.</span>
        </MessageBox>
      ) : !record ? (
        <MessageBox>
          <span>No data available.</span>
        </MessageBox>
      ) : (
        <Component title={title} dimensions={dimensions} measures={measures} record={record} />
      )}
    </div>
  );
}
